import type {
  SectionKey,
  Case,
  SiteOfCareInfo,
  AdaptedCase,
  ParsedSection,
  ObjectionRebuttal,
  FontSize,
  MedicationEntry,
} from "../types/caseView";

// ============================================
// COLLAPSIBLE SECTION SYSTEM
// ============================================

// Map section titles to stable keys
export function getSectionKey(
  title: string,
  iconType: string
): SectionKey | null {
  const normalizedTitle = title.toLowerCase().trim();

  if (
    normalizedTitle.includes("opening statement") ||
    normalizedTitle === "opening"
  ) {
    return "opening_statement";
  }
  if (normalizedTitle.includes("call script")) {
    return "call_scripts";
  }
  if (
    normalizedTitle.includes("key talking points") ||
    normalizedTitle.includes("talking points")
  ) {
    return "key_talking_points";
  }
  if (
    normalizedTitle.includes("criteria mapping") ||
    normalizedTitle.includes("criteria")
  ) {
    return "criteria_mapping";
  }
  if (
    normalizedTitle.includes("evidence") ||
    normalizedTitle.includes("citation")
  ) {
    return "evidence_to_cite";
  }
  if (
    normalizedTitle.includes("quick reference") ||
    normalizedTitle.includes("quick ref")
  ) {
    return "quick_reference_card";
  }
  if (normalizedTitle.includes("risk") && normalizedTitle.includes("denied")) {
    return "risk_if_denied";
  }

  // Accountability-based sections
  if (normalizedTitle.includes("medical reasoning question")) {
    return "medical_reasoning_questions";
  }
  if (
    normalizedTitle.includes("insurer alternative") ||
    normalizedTitle.includes("alternative plan")
  ) {
    return "insurer_alternative_plan";
  }
  if (
    normalizedTitle.includes("time-sensitive") ||
    normalizedTitle.includes("risk window")
  ) {
    return "time_sensitive_risk_window";
  }
  if (
    normalizedTitle.includes("foreseeable") ||
    normalizedTitle.includes("clinical course without")
  ) {
    return "foreseeable_clinical_course";
  }
  if (normalizedTitle.includes("documentation clarification")) {
    return "documentation_clarification";
  }

  // Fallback based on icon type
  if (iconType === "talking" && !normalizedTitle.includes("opening")) {
    return "key_talking_points";
  }
  if (iconType === "criteria") return "criteria_mapping";
  if (iconType === "evidence") return "evidence_to_cite";
  if (iconType === "quick") return "quick_reference_card";
  if (iconType === "risk") return "risk_if_denied";

  return null;
}

// Default expanded sections — Timeline is the hero workspace in Prep Mode
export const DEFAULT_EXPANDED_SECTIONS: Set<SectionKey> = new Set([
  "timeline_builder",
]);

// All section keys for expand/collapse all
export const ALL_SECTION_KEYS: SectionKey[] = [
  "opening_statement",
  "call_scripts",
  "key_talking_points",
  "criteria_mapping",
  "evidence_to_cite",
  "quick_reference_card",
  "risk_if_denied",
  "coverage_requirements",
  "rebuttals_decision_tree",
  "timeline_builder",
  // Accountability-based sections
  "medical_reasoning_questions",
  "insurer_alternative_plan",
  "time_sensitive_risk_window",
  "foreseeable_clinical_course",
  "documentation_clarification",
];

// LocalStorage key helper
export function getStorageKey(caseId: string): string {
  return `peerprep.caseview.sections.v1:${caseId}`;
}

// Load expanded state from localStorage
export function loadExpandedState(caseId: string): Set<SectionKey> {
  try {
    const stored = localStorage.getItem(getStorageKey(caseId));
    if (stored) {
      const parsed = JSON.parse(stored) as Record<SectionKey, boolean>;
      const expanded = new Set<SectionKey>();
      for (const [key, isExpanded] of Object.entries(parsed)) {
        if (isExpanded) {
          expanded.add(key as SectionKey);
        }
      }
      return expanded;
    }
  } catch (e) {
    console.warn("Failed to load section state from localStorage:", e);
  }
  return new Set(DEFAULT_EXPANDED_SECTIONS);
}

// Save expanded state to localStorage
export function saveExpandedState(
  caseId: string,
  expanded: Set<SectionKey>
): void {
  try {
    const state: Record<string, boolean> = {};
    for (const key of ALL_SECTION_KEYS) {
      state[key] = expanded.has(key);
    }
    localStorage.setItem(getStorageKey(caseId), JSON.stringify(state));
  } catch (e) {
    console.warn("Failed to save section state to localStorage:", e);
  }
}

// ============================================
// SITE OF CARE CONSTANTS (matching NewCase.tsx)
// ============================================

export const FACILITY_TYPES = [
  { id: "hospital", label: "Hospital / Inpatient", icon: "🏥" },
  { id: "asc", label: "Ambulatory Surgery Center", icon: "🏢" },
  { id: "imaging_center", label: "Imaging Center", icon: "📷" },
  { id: "office", label: "Office / Clinic", icon: "🩺" },
  { id: "home", label: "Home Infusion / Home Health", icon: "🏠" },
  { id: "snf", label: "Skilled Nursing Facility", icon: "🏨" },
  { id: "not_sure", label: "Not sure / Other", icon: "❓" },
] as const;

export const HOSPITAL_JUSTIFICATION_FACTORS = [
  {
    id: "anesthesia_risk",
    label: "General anesthesia or MAC required",
    category: "anesthesia",
  },
  {
    id: "high_bmi",
    label: "BMI > 40 (equipment/monitoring needs)",
    category: "patient",
  },
  {
    id: "complex_procedure",
    label: "Procedure complexity/duration > 2 hours",
    category: "procedure",
  },
  {
    id: "icu_monitoring",
    label: "Post-procedure ICU/step-down monitoring needed",
    category: "monitoring",
  },
  {
    id: "subspecialty_backup",
    label: "Subspecialty backup required (e.g., vascular surgery)",
    category: "procedure",
  },
  {
    id: "specialized_equipment",
    label: "Specialized equipment only available in hospital",
    category: "equipment",
  },
  {
    id: "prior_complications",
    label: "History of procedural complications",
    category: "patient",
  },
  {
    id: "cardiac_risk",
    label: "Significant cardiac risk factors",
    category: "patient",
  },
  {
    id: "anticoagulation",
    label: "Complex anticoagulation management",
    category: "patient",
  },
  {
    id: "pregnancy_postpartum",
    label: "Pregnancy or immediate postpartum",
    category: "patient",
  },
  {
    id: "comorbidities",
    label: "Multiple comorbidities requiring coordination",
    category: "patient",
  },
  { id: "other", label: "Other clinical justification", category: "other" },
] as const;

// ============================================
// HELPER FUNCTIONS
// ============================================

// Convert title to Title Case
export function toTitleCase(str: string): string {
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Parse site-of-care information from additional_context
 * Looks for patterns like "FACILITY REQUESTED: Hospital" and "Hospital Justification Factors:"
 */
export function parseSiteOfCareInfo(
  additionalContext: string | null | undefined
): SiteOfCareInfo {
  const result: SiteOfCareInfo = {
    facilityType: null,
    facilityLabel: null,
    hospitalJustifications: [],
    justificationLabels: [],
    otherJustification: null,
  };

  if (!additionalContext) return result;

  // Parse facility type
  const facilityMatch = additionalContext.match(
    /FACILITY REQUESTED:\s*([^\n]+)/i
  );
  if (facilityMatch) {
    const facilityText = facilityMatch[1].trim();
    // Find matching facility type
    const facility = FACILITY_TYPES.find(
      (f) =>
        facilityText.toLowerCase().includes(f.label.toLowerCase()) ||
        facilityText.toLowerCase().includes(f.id.toLowerCase())
    );
    if (facility) {
      result.facilityType = facility.id;
      result.facilityLabel = facility.label;
    } else {
      result.facilityLabel = facilityText;
    }
  }

  // Parse hospital justification factors
  const justificationSection = additionalContext.match(
    /Hospital Justification Factors:\s*([\s\S]*?)(?=\n\n|$)/i
  );
  if (justificationSection) {
    const justificationText = justificationSection[1];
    // Extract bullet points
    const bullets = justificationText.match(/[•\-]\s*([^\n]+)/g);
    if (bullets) {
      bullets.forEach((bullet) => {
        const text = bullet.replace(/^[•\-]\s*/, "").trim();
        // Find matching justification factor
        const factor = HOSPITAL_JUSTIFICATION_FACTORS.find((f) =>
          text.toLowerCase().includes(f.label.toLowerCase())
        );
        if (factor) {
          result.hospitalJustifications.push(factor.id);
          result.justificationLabels.push(factor.label);
        } else if (text.toLowerCase().startsWith("other:")) {
          result.hospitalJustifications.push("other");
          result.otherJustification = text.replace(/^other:\s*/i, "").trim();
          result.justificationLabels.push(
            `Other: ${result.otherJustification}`
          );
        }
      });
    }
  }

  return result;
}

/**
 * Check if this is a site-of-care denial case
 */
export function isSiteOfCareDenial(caseData: Case | null): boolean {
  if (!caseData) return false;
  const denialReason = caseData.denial_reason?.toLowerCase() || "";
  return (
    denialReason.includes("site of care") || denialReason.includes("facility")
  );
}

/**
 * Extract medication name from case data
 * Looks for patterns like "Medication: Journavx" or drug-like names in various fields
 */
export function extractMedicationFromCase(
  caseData: Case | null
): string | undefined {
  if (!caseData) {
    console.log("[CaseView] No caseData for medication extraction");
    return undefined;
  }

  console.log("[CaseView] Extracting medication from case:");
  console.log("[CaseView] - friendly_name:", caseData.friendly_name);
  console.log("[CaseView] - request_details:", caseData.request_details);

  // Pattern for drug-like names: Capitalized, 5+ chars, common suffixes
  const drugPattern =
    /\b([A-Z][a-z]{4,}(?:vx|vax|mab|nib|zole|ine|ide|pril|olol|sartan|statin|glutide|gliflozin)?)\b/;

  // PRIORITY 1: Check friendly_name (case title)
  if (caseData.friendly_name) {
    const nameMatch = caseData.friendly_name.match(drugPattern);
    if (nameMatch) {
      console.log(
        "[CaseView] ✅ Found medication in friendly_name:",
        nameMatch[1]
      );
      return nameMatch[1];
    }
  }

  // PRIORITY 2: Check request_details with "Medication: X" pattern
  if (caseData.request_details) {
    const medMatch = caseData.request_details.match(
      /Medication:\s*([A-Za-z0-9-]+)/i
    );
    if (medMatch) {
      console.log(
        "[CaseView] ✅ Found medication via 'Medication:' pattern:",
        medMatch[1]
      );
      return medMatch[1];
    }

    // Try drug pattern
    const drugMatch = caseData.request_details.match(drugPattern);
    if (drugMatch) {
      console.log(
        "[CaseView] ✅ Found drug-like word in request_details:",
        drugMatch[1]
      );
      return drugMatch[1];
    }
  }

  // PRIORITY 3: Check diagnoses
  if (caseData.diagnoses) {
    const diagMatch = caseData.diagnoses.match(drugPattern);
    if (diagMatch) {
      console.log("[CaseView] ✅ Found medication in diagnoses:", diagMatch[1]);
      return diagMatch[1];
    }
  }

  console.log("[CaseView] ❌ Could not extract medication from case");
  return undefined;
}

/**
 * Parse prior medications from case data
 */
export function parsePriorMedications(
  caseData: Case | null
): MedicationEntry[] {
  if (!caseData?.prior_medications) return [];
  try {
    return JSON.parse(caseData.prior_medications);
  } catch {
    return [];
  }
}

export function parseAgeRange(ageRange: string): number | undefined {
  const match = ageRange?.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : undefined;
}

export function parseTriedTreatments(triedAlready: string): string[] {
  if (!triedAlready) return [];
  return triedAlready
    .split(/[,;\n]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);
}

export function adaptCaseForComponents(caseData: Case): AdaptedCase {
  return {
    id: caseData.id,
    case_name: caseData.friendly_name,
    insurance_company: caseData.insurance_company,
    plan_type: caseData.plan_type,
    diagnoses: caseData.diagnoses,
    request_type: caseData.request_type,
    request_details: caseData.request_details,
    denial_reason: caseData.denial_reason,
    denial_details: caseData.denial_details,
    patient_age: parseAgeRange(caseData.age_range),
    tried_treatments: parseTriedTreatments(caseData.tried_already),
    key_facts: caseData.key_facts || "",
    additional_context: caseData.additional_context || "",
    comorbidities: caseData.comorbidities
      ? caseData.comorbidities
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean)
      : [],
    ai_output: caseData.ai_prep || undefined,
  };
}

// ============================================
// AI PREP PARSING
// ============================================

export function parseObjectionsAndRebuttals(
  content: string
): ObjectionRebuttal[] {
  const pairs: ObjectionRebuttal[] = [];

  // Try Format 1: **OBJECTION:** ... **REBUTTAL:** ... **IF THEY PUSH BACK:** ...
  const objectionBlocks = content.split(/(?=\*\*OBJECTION\*?\*?:)/gi);

  for (const block of objectionBlocks) {
    if (!block.trim()) continue;

    const objectionMatch = block.match(
      /\*\*OBJECTION\*?\*?:\*?\*?\s*(.+?)(?=\*\*REBUTTAL|$)/is
    );
    const rebuttalMatch = block.match(
      /\*\*REBUTTAL\*?\*?:\*?\*?\s*(.+?)(?=\*\*IF THEY PUSH BACK|$)/is
    );
    const escalationMatch = block.match(
      /\*\*IF THEY PUSH BACK\*?\*?:\*?\*?\s*(.+?)(?=\*\*OBJECTION|$)/is
    );

    if (objectionMatch) {
      pairs.push({
        objection: objectionMatch[1]
          .trim()
          .replace(/\*\*/g, "")
          .replace(/["'\u2018\u2019\u201C\u201D]/g, ""),
        rebuttal: rebuttalMatch
          ? rebuttalMatch[1].trim().replace(/\*\*/g, "")
          : "",
        escalation: escalationMatch
          ? escalationMatch[1].trim().replace(/\*\*/g, "")
          : undefined,
      });
    }
  }

  // Try Format 2: Numbered items with "Objection:" and "Response/Rebuttal:" labels
  if (pairs.length === 0) {
    const numberedPattern =
      /(?:^|\n)(?:\d+\.\s*|\-\s*)?\*?\*?(?:Objection|They (?:may |might )?say)[:\*]*\*?\s*[\u201C\u201D"]?([^\u201C\u201D"\n]+)[\u201C\u201D"]?(?:\n|\s)*\*?\*?(?:Rebuttal|Response|Your response|Say)[:\*]*\*?\s*([^\n]+(?:\n(?!\d+\.|\-\s*\*?\*?(?:Objection|They)).*)*)/gi;

    let match;
    while ((match = numberedPattern.exec(content)) !== null) {
      if (match[1] && match[2]) {
        pairs.push({
          objection: match[1]
            .trim()
            .replace(/\*\*/g, "")
            .replace(/["'\u2018\u2019\u201C\u201D]/g, ""),
          rebuttal: match[2].trim().replace(/\*\*/g, ""),
        });
      }
    }
  }

  // Try Format 3: Simple bullet/number pairs
  if (pairs.length === 0) {
    const lines = content.split("\n").filter((l) => l.trim());
    let currentObjection = "";
    let currentRebuttal = "";

    for (const line of lines) {
      const cleanLine = line
        .replace(/^\d+\.\s*/, "")
        .replace(/^-\s*/, "")
        .replace(/^\*\s*/, "")
        .trim();

      if (
        cleanLine.toLowerCase().startsWith("objection") ||
        cleanLine.toLowerCase().startsWith("they say") ||
        cleanLine.toLowerCase().startsWith("they may say") ||
        cleanLine.toLowerCase().startsWith("they might say") ||
        cleanLine.toLowerCase().startsWith('"') ||
        cleanLine.match(/^["']/)
      ) {
        if (currentObjection) {
          pairs.push({
            objection: currentObjection,
            rebuttal: currentRebuttal,
          });
        }
        currentObjection = cleanLine
          .replace(/^objection:?\s*/i, "")
          .replace(/^they (?:may |might )?say:?\s*/i, "")
          .replace(/["'\u2018\u2019\u201C\u201D]/g, "");
        currentRebuttal = "";
      } else if (
        cleanLine.toLowerCase().startsWith("rebuttal") ||
        cleanLine.toLowerCase().startsWith("response") ||
        cleanLine.toLowerCase().startsWith("say:") ||
        cleanLine.toLowerCase().startsWith("your response")
      ) {
        currentRebuttal = cleanLine
          .replace(/^rebuttal:?\s*/i, "")
          .replace(/^response:?\s*/i, "")
          .replace(/^say:?\s*/i, "")
          .replace(/^your response:?\s*/i, "");
      } else if (currentObjection && !currentRebuttal) {
        currentObjection += " " + cleanLine;
      } else if (currentRebuttal) {
        currentRebuttal += " " + cleanLine;
      }
    }
    if (currentObjection) {
      pairs.push({ objection: currentObjection, rebuttal: currentRebuttal });
    }
  }

  return pairs;
}

export function parseObjectionsFromSeparateSections(
  aiPrep: string
): ObjectionRebuttal[] {
  const pairs: ObjectionRebuttal[] = [];

  // ── FORMAT 1: New structured format with ### OBJECTION: headers ──
  // Matches: ## ANTICIPATED OBJECTIONS & REBUTTALS  or  ## ANTICIPATED OBJECTIONS AND REBUTTALS
  const combinedMatch = aiPrep.match(
    /##\s*ANTICIPATED OBJECTIONS\s*(?:&|AND)\s*REBUTTALS\s*\n([\s\S]*?)(?=\n##\s(?!##)|$)/i
  );

  if (combinedMatch) {
    const content = combinedMatch[1];
    // Split by ### OBJECTION: headers
    const objectionBlocks = content
      .split(/(?=###\s*OBJECTION\s*:)/i)
      .filter((b) => b.trim());

    for (const block of objectionBlocks) {
      const headerMatch = block.match(/###\s*OBJECTION\s*:\s*(.+)/i);
      if (!headerMatch) continue;

      const objection = headerMatch[1].replace(/\*\*/g, "").trim();

      // Extract ONE-LINER
      const oneLinerMatch = block.match(/\*\*ONE[\s-]?LINER\*?\*?:\s*(.+)/i);
      const oneLiner = oneLinerMatch ? oneLinerMatch[1].trim() : "";

      // Extract BULLETS
      const bulletsMatch = block.match(
        /\*\*BULLETS\*?\*?:\s*\n([\s\S]*?)(?=\n\*\*(?:FULL|ESCALATION|PERSONALIZED)|$)/i
      );
      const bullets: string[] = [];
      if (bulletsMatch) {
        const bulletLines = bulletsMatch[1].split("\n");
        for (const line of bulletLines) {
          const bulletMatch = line.match(/^\s*[-*]\s+(.+)/);
          if (bulletMatch) {
            bullets.push(bulletMatch[1].replace(/\*\*/g, "").trim());
          }
        }
      }

      // Extract FULL
      const fullMatch = block.match(
        /\*\*FULL\*?\*?:\s*([\s\S]*?)(?=\n\*\*(?:ESCALATION|PERSONALIZED|INJECTED)|$)/i
      );
      const fullParagraph = fullMatch
        ? fullMatch[1].replace(/\*\*/g, "").trim()
        : "";

      // Extract ESCALATION
      const escalationMatch = block.match(
        /\*\*ESCALATION\*?\*?:\s*([\s\S]*?)(?=\n\*\*(?:PERSONALIZED|INJECTED)|$)/i
      );
      const escalation = escalationMatch
        ? escalationMatch[1].replace(/\*\*/g, "").trim()
        : undefined;

      // Extract PERSONALIZED flag
      const personalizedMatch = block.match(
        /\*\*PERSONALIZED\*?\*?:\s*(yes|no)/i
      );
      const isPersonalized = personalizedMatch
        ? personalizedMatch[1].toLowerCase() === "yes"
        : false;

      // Extract INJECTED_FIELDS
      const injectedMatch = block.match(
        /\*\*INJECTED[\s_]?FIELDS\*?\*?:\s*(.+)/i
      );
      const injectedFields = injectedMatch
        ? injectedMatch[1]
            .split(",")
            .map((f) => f.trim())
            .filter(Boolean)
        : [];

      // Build the rebuttal — use full paragraph as primary for backward compat
      const rebuttal = fullParagraph || oneLiner || bullets.join(". ");

      pairs.push({
        objection,
        rebuttal,
        escalation: escalation || undefined,
        variants: {
          one_liner: oneLiner,
          bullets,
          full_paragraph: fullParagraph,
        },
        personalization: {
          is_personalized: isPersonalized,
          injected_fields: injectedFields,
          placeholder_fields: [], // future use
        },
      });
    }
  }

  // ── FORMAT 2: Legacy separate sections (backward compat) ──
  if (pairs.length === 0) {
    const objectionsMatch = aiPrep.match(
      /##\s*ANTICIPATED OBJECTIONS\s*\n([\s\S]*?)(?=\n##\s|$)/i
    );
    const rebuttalsMatch = aiPrep.match(
      /##\s*REBUTTALS\s*\n([\s\S]*?)(?=\n##\s|$)/i
    );

    if (!objectionsMatch) return pairs;

    const objectionsContent = objectionsMatch[1];
    const rebuttalsContent = rebuttalsMatch ? rebuttalsMatch[1] : "";

    // Extract objection lines — numbered, bulleted, or starred
    const objectionLines: string[] = [];
    const objLinePattern =
      /^(?:\d+\.\s*|[-*]\s+)[\u201C\u201D"]?(.+?)[\u201C\u201D"]?\s*$/gm;
    let objMatch;
    while ((objMatch = objLinePattern.exec(objectionsContent)) !== null) {
      const text = objMatch[1].replace(/\*\*/g, "").trim();
      if (text) objectionLines.push(text);
    }

    // Extract rebuttals — try arrow format first
    const rebuttalBlocks: string[] = [];

    // Format A: "**Objection → Rebuttal**: rebuttal text" (arrow pattern)
    const arrowPattern =
      /^(?:\d+\.\s*|[-*]\s+)\*\*[^*]+→\s*Rebuttal\*?\*?:\s*(.+)$/gm;
    let arrowMatch;
    while ((arrowMatch = arrowPattern.exec(rebuttalsContent)) !== null) {
      const text = arrowMatch[1].replace(/\*\*/g, "").trim();
      if (text) rebuttalBlocks.push(text);
    }

    // Format B: Numbered/bulleted sections (fallback if Format A found nothing)
    if (rebuttalBlocks.length === 0) {
      const rebuttalSections = rebuttalsContent
        .split(/(?=^\d+\.\s)/m)
        .filter((s) => s.trim());

      for (const section of rebuttalSections) {
        const cleaned = section.replace(/^\d+\.\s*/, "");
        const lines = cleaned
          .split("\n")
          .map((l) => l.trim())
          .filter((l) => l);
        const textParts: string[] = [];
        for (const line of lines) {
          const cleanedLine = line
            .replace(/^\*\*([^*]+)\*\*:?\s*/, "$1: ")
            .replace(/^-\s*/, "")
            .trim();
          if (cleanedLine) textParts.push(cleanedLine);
        }
        if (textParts.length > 0) rebuttalBlocks.push(textParts.join(" "));
      }
    }

    // Format C: Simple bullet rebuttals (if still nothing)
    if (rebuttalBlocks.length === 0) {
      const bulletPattern = /^(?:[-*]\s+)(.+)$/gm;
      let bulletMatch;
      while ((bulletMatch = bulletPattern.exec(rebuttalsContent)) !== null) {
        const text = bulletMatch[1]
          .replace(/\*\*[^*]+→\s*Rebuttal\*?\*?:\s*/i, "")
          .replace(/\*\*/g, "")
          .trim();
        if (text) rebuttalBlocks.push(text);
      }
    }

    // Pair and auto-derive variants from single rebuttal (graceful degradation)
    for (let i = 0; i < objectionLines.length; i++) {
      const rebuttal = rebuttalBlocks[i] || "";
      const sentences = rebuttal
        .split(/(?<=[.!?])\s+/)
        .filter((s) => s.trim().length > 5);

      pairs.push({
        objection: objectionLines[i],
        rebuttal,
        variants: {
          one_liner: sentences[0]?.slice(0, 160) || rebuttal.slice(0, 160),
          bullets:
            sentences.length > 1
              ? sentences.slice(0, 5).map((s) => s.trim())
              : [rebuttal],
          full_paragraph: rebuttal,
        },
        personalization: {
          is_personalized: false,
          injected_fields: [],
          placeholder_fields: [],
        },
      });
    }
  }

  return pairs;
}

// ============================================
// SECTION CONFIG — maps AI output headers to icon types
// ============================================

const SECTION_CONFIG: Record<
  string,
  | "talking"
  | "criteria"
  | "evidence"
  | "quick"
  | "risk"
  | "appeal"
  | "objection"
> = {
  // Existing sections
  "OPENING STATEMENT": "talking",
  "KEY TALKING POINTS": "talking",
  "CRITERIA MAPPING": "criteria",
  "EVIDENCE & CITATIONS": "evidence",
  "EVIDENCE AND CITATIONS": "evidence",
  "RISK IF DENIED": "risk",
  "QUICK REFERENCE CARD": "quick",
  "QUICK REFERENCE": "quick",
  "IF THE P2P FAILS": "appeal",
  "IF P2P FAILS": "appeal",
  "ANTICIPATED OBJECTIONS & REBUTTALS": "objection",
  "ANTICIPATED OBJECTIONS AND REBUTTALS": "objection",
  "RELEVANT CRITERIA": "criteria",
  "EVIDENCE TO CITE": "evidence",
  "ANTICIPATED OBJECTIONS": "objection",
  REBUTTALS: "objection",

  // Accountability-based sections
  "MEDICAL REASONING QUESTIONS": "talking",
  "INSURER ALTERNATIVE PLAN": "talking",
  "TIME-SENSITIVE RISK WINDOW": "risk",
  "FORESEEABLE CLINICAL COURSE WITHOUT APPROVAL": "risk",
  "DOCUMENTATION CLARIFICATION QUESTIONS": "appeal",
};

export function getIconTypeForSection(
  title: string
):
  | "talking"
  | "criteria"
  | "evidence"
  | "quick"
  | "risk"
  | "appeal"
  | "objection" {
  const normalizedTitle = title.toUpperCase().trim();

  // Direct match
  if (SECTION_CONFIG[normalizedTitle]) {
    return SECTION_CONFIG[normalizedTitle];
  }

  // Partial match
  for (const [key, value] of Object.entries(SECTION_CONFIG)) {
    if (normalizedTitle.includes(key) || key.includes(normalizedTitle)) {
      return value;
    }
  }

  // Fallback heuristics
  if (
    normalizedTitle.includes("TALK") ||
    normalizedTitle.includes("OPEN") ||
    normalizedTitle.includes("STATEMENT") ||
    normalizedTitle.includes("REASONING QUESTION") ||
    normalizedTitle.includes("ALTERNATIVE PLAN")
  ) {
    return "talking";
  }
  if (normalizedTitle.includes("CRITERIA") || normalizedTitle.includes("MAP")) {
    return "criteria";
  }
  if (
    normalizedTitle.includes("EVIDENCE") ||
    normalizedTitle.includes("CITATION") ||
    normalizedTitle.includes("REFERENCE")
  ) {
    return "evidence";
  }
  if (
    normalizedTitle.includes("QUICK") ||
    normalizedTitle.includes("CARD") ||
    normalizedTitle.includes("CHEAT")
  ) {
    return "quick";
  }
  if (
    normalizedTitle.includes("RISK") ||
    normalizedTitle.includes("DENIED") ||
    normalizedTitle.includes("CONSEQUENCE") ||
    normalizedTitle.includes("FORESEEABLE") ||
    normalizedTitle.includes("CLINICAL COURSE") ||
    normalizedTitle.includes("TIME-SENSITIVE")
  ) {
    return "risk";
  }
  if (
    normalizedTitle.includes("FAIL") ||
    normalizedTitle.includes("APPEAL") ||
    normalizedTitle.includes("DOCUMENT") ||
    normalizedTitle.includes("CLARIFICATION")
  ) {
    return "appeal";
  }
  if (
    normalizedTitle.includes("OBJECTION") ||
    normalizedTitle.includes("REBUTTAL")
  ) {
    return "objection";
  }

  return "talking";
}

export function parseAIPrep(aiPrep: string): {
  sections: ParsedSection[];
  objectionPairs: ObjectionRebuttal[];
} {
  const sections: ParsedSection[] = [];
  let objectionPairs: ObjectionRebuttal[] = [];

  objectionPairs = parseObjectionsFromSeparateSections(aiPrep);

  const parts = aiPrep.split(/(?=^###?\s+)/m);

  for (const part of parts) {
    const match = part.match(/^###?\s+(.+?)[\n\r]/);
    if (match) {
      const title = match[1].trim();
      const content = part.replace(/^###?\s+.+?[\n\r]/, "").trim();
      const normalizedTitle = title.toUpperCase();

      // Skip combined objections & rebuttals section (already parsed above)
      if (
        normalizedTitle.includes("OBJECTION") &&
        normalizedTitle.includes("REBUTTAL")
      ) {
        if (objectionPairs.length === 0) {
          objectionPairs = parseObjectionsAndRebuttals(content);
        }
        if (objectionPairs.length === 0) {
          sections.push({
            title,
            content,
            iconType: "objection",
          });
        }
        continue;
      }

      // Skip standalone objection/rebuttal sections (already parsed above)
      if (
        normalizedTitle === "REBUTTALS" ||
        normalizedTitle === "ANTICIPATED OBJECTIONS" ||
        (normalizedTitle.includes("ANTICIPATED OBJECTIONS") &&
          normalizedTitle.includes("REBUTTALS"))
      ) {
        continue;
      }

      // Skip individual ### OBJECTION: sub-headers (they're inside the combined section)
      if (normalizedTitle.startsWith("OBJECTION:")) {
        continue;
      }

      const iconType = getIconTypeForSection(title);
      sections.push({ title, content, iconType });
    }
  }

  return { sections, objectionPairs };
}

export function getFontSizeClasses(
  fontSize: FontSize,
  callMode: boolean
): string {
  const sizes = {
    small: callMode ? "text-base" : "text-sm",
    medium: callMode ? "text-lg" : "text-base",
    large: callMode ? "text-xl" : "text-lg",
  };
  return sizes[fontSize];
}
